/**
 * Semáforo de mora de los préstamos. Cada empresa elige sus colores en el portal («Mi empresa»);
 * el API los manda al iniciar sesión y en `/user` (`overdueColors`), igual que la zona horaria.
 * Se guardan en el navegador para tenerlos al recargar. Sin configurar: los colores de siempre.
 */
export interface OverdueBand {
  /** Desde cuántos días de atraso aplica. */
  minDays: number;
  /** Color de fondo #RRGGBB. */
  color: string;
}

export interface OverdueTone {
  background: string;
  text: string;
}

/** Amarillo (11–20 días), azul (21–30), verde (31–40) y morado (41 o más). Igual que el API. */
export const DEFAULT_OVERDUE_BANDS: OverdueBand[] = [
  { minDays: 11, color: '#FDD835' },
  { minDays: 21, color: '#1E6FD9' },
  { minDays: 31, color: '#2E7D32' },
  { minDays: 41, color: '#7B1FA2' },
];

const STORAGE_KEY = 'ruttia.overdueColors';
const HEX = /^#[0-9a-f]{6}$/i;
const DARK_TEXT = '#1B2430';
const LIGHT_TEXT = '#FFFFFF';

const isValid = (value: unknown): value is OverdueBand[] =>
  Array.isArray(value) &&
  value.length > 0 &&
  value.length <= 6 &&
  value.every(
    (b, i) =>
      Number.isInteger(b?.minDays) &&
      b.minDays >= 1 &&
      typeof b?.color === 'string' &&
      HEX.test(b.color) &&
      (i === 0 || b.minDays > value[i - 1].minDays),
  );

let bands: OverdueBand[] = (() => {
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
    if (isValid(saved)) return saved;
  } catch {
    /* sin almacenamiento o dato dañado: los de siempre */
  }
  return DEFAULT_OVERDUE_BANDS;
})();

/** Guarda lo que manda el API (si no es válido, se ignora). */
export const setOverdueBands = (value: unknown): void => {
  if (!isValid(value)) return;
  bands = value.map((b) => ({ minDays: b.minDays, color: b.color.toUpperCase() }));
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(bands));
  } catch {
    /* sin almacenamiento: dura hasta recargar */
  }
};

export const getOverdueBands = (): OverdueBand[] => bands;

const luminance = (hex: string) => {
  const [r, g, b] = [1, 3, 5].map((i) => {
    const c = parseInt(hex.slice(i, i + 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

const contrast = (a: string, b: string) => {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
};

/** Texto blanco u oscuro, el que más se lea sobre ese color. */
export const readableTextOn = (background: string): string =>
  contrast(background, LIGHT_TEXT) >= contrast(background, DARK_TEXT) ? LIGHT_TEXT : DARK_TEXT;

/** Color del semáforo para unos días de atraso, o null si aún no llega al primer tramo. */
export const overdueToneFor = (days: number, list: OverdueBand[] = bands): OverdueTone | null => {
  let found: OverdueBand | null = null;
  for (const band of list) if (days >= band.minDays) found = band;
  return found ? { background: found.color, text: readableTextOn(found.color) } : null;
};
